import type { ReportColumnDef } from '@/reports/types';

function escapeCsv(value: unknown): string {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function downloadCsv(
  filename: string,
  columns: ReportColumnDef[],
  rows: Record<string, unknown>[]
): void {
  const header = columns.map((c) => escapeCsv(c.title)).join(',');
  const lines = rows.map((row) => columns.map((c) => escapeCsv(row[c.dataIndex])).join(','));
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}

export async function downloadXlsx(
  filename: string,
  columns: ReportColumnDef[],
  rows: Record<string, unknown>[]
): Promise<void> {
  const XLSX = await import('xlsx');
  const aoa = [
    columns.map((c) => c.title),
    ...rows.map((row) => columns.map((c) => row[c.dataIndex] ?? '')),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

/** Revocation is delayed: some browsers abort the download if the object URL is revoked synchronously. */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Opens a blob (PDF) in a new tab; the URL is kept alive long enough for the viewer to load it. */
export function openBlobInNewTab(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
